/**
 * <palm-overlay-legend> — the rekhā colours with their Sanskrit and English names,
 * and for each line whether it has been traced and how — as a visible bar.
 *
 * ◆ Colour is never the only carrier: every entry has a swatch *and* a name, and
 * the measured length of a traced line is drawn as a bar with its number beside it.
 * The bar is a **measurement of the reader's own tracing** (length as a share of
 * the canvas width), not a detection confidence — nothing here is detected.
 *
 * Set `.measurements` (from <palm-canvas>.measurements()) and `.active` (the id
 * of the line being traced). A click on an entry fires `line-select`.
 *
 * © Manish Jagdish Thatte
 */

import { LINES } from '../hasta/reference.js';
import { bar, h, nz } from '../utils/dom.js';

export class PalmOverlayLegend extends HTMLElement {
  connectedCallback() { this.render(); }

  set measurements(value) { this._m = value ?? {}; this.render(); }
  set active(id) { this._active = id; this.render(); }

  /** Detected lines: `{ [referenceId]: { confidence, low } }`. Shown as a bar beside the traced length. */
  set detections(value) { this._d = value ?? {}; this.render(); }

  render() {
    this.textContent = '';
    const m = this._m ?? {};
    const rows = LINES.map((l) => {
      const t = m[l.id];
      const d = this._d?.[l.id];
      const pressed = this._active === l.id;
      const btn = h('button', {
        type: 'button', class: `palm-legend__line${pressed ? ' is-active' : ''}`, 'aria-pressed': String(pressed),
        onclick: () => this.dispatchEvent(new CustomEvent('line-select', { detail: { id: l.id }, bubbles: true }))
      },
      h('span', { class: 'palm-legend__swatch', style: `background:var(${l.colour}, ${l.fallback})`, 'aria-hidden': 'true' }),
      h('span', { class: 'palm-legend__name' }, l.name, h('span', { lang: 'sa' }, ` ${l.sa}`), h('span', { class: 'palm-legend__en' }, ` — ${l.english}`)));
      return h('li', {}, btn,
        d ? h('span', { class: 'palm-legend__measure' }, bar(d.confidence, `${l.english}, detection confidence`), d.low ? h('strong', {}, ' low') : null) : null,
        t ? h('span', { class: 'palm-legend__measure' },
          bar(Math.min(1, t.length), `${l.english}, traced length`),
          h('span', { class: 'field-note' }, nz(` ${t.strokes} stroke${t.strokes === 1 ? '' : 's'}`)))
          : h('span', { class: 'field-note' }, 'not traced'));
    });
    this.append(h('ul', { class: 'palm-legend' }, rows));
  }
}

customElements.define('palm-overlay-legend', PalmOverlayLegend);
